import './Notification.scss';

import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { CSSProperties, ReactElement, useEffect, useState } from 'react';

/**
 * Notification type enum for consistent typing across the application
 */
export type NotificationType = 'success' | 'error' | 'info' | 'warning';

/**
 * Props for the Notification component
 */
export interface NotificationProps {
  /**
   * Message to display in the notification
   */
  message: string;

  /**
   * Duration in milliseconds to show the notification before auto-hiding
   * Default: 2000ms (2 seconds)
   */
  duration?: number;

  /**
   * Whether the notification is visible
   */
  visible: boolean;

  /**
   * Callback function when notification is closed
   */
  onClose?: () => void;

  /**
   * Type of notification (affects styling)
   * Default: 'success'
   */
  type?: NotificationType;

  /**
   * Optional unique notification identifier, which can be used
   * to more easily identify a notification in a collection
   */
  notificationId?: string;

  /**
   * Optional inline styles to override default positioning due to
   * the need to dynamically position the Notification as an override
   */
  style?: CSSProperties;
}

/**
 * Notification component that appears in the bottom right corner
 * Used for displaying temporary messages to the user
 *
 * @param props - Component props
 * @returns Notification component or null if not visible
 */
const Notification = ({
  message,
  duration = 5000, // Note: Some mocks suggest 5 seconds, so increasing to this as the default
  visible,
  onClose,
  type = 'success',
  notificationId,
  style,
}: NotificationProps): ReactElement | null => {
  const [isVisible, setIsVisible] = useState<boolean>(visible);

  useEffect(() => {
    setIsVisible(visible);

    if (visible && duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose(); // Invoke onClose callback if exists
      }, duration);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [visible, duration, onClose]);

  if (!isVisible) return null;

  const notificationTypeIcons = {
    success: <CheckCircleOutlinedIcon className='notification-icon' />,
    info: <InfoIcon className='notification-icon' />,
    warning: <WarningAmberIcon className='notification-icon' />,
    error: <ErrorIcon className='notification-icon' />,
    unknownType: <QuestionMarkIcon className='notification-icon' />,
  };

  return (
    <Box className={`notification notification-${type}`} id={`notification-${notificationId || 'no-id'}`} style={style}>
      <Box className='notification-content'>
        {notificationTypeIcons[type] || notificationTypeIcons.unknownType}
        <Typography className='notification-message'>{message}</Typography>
      </Box>
      <IconButton
        className='notification-close'
        onClick={() => {
          setIsVisible(false);
          if (onClose) onClose();
        }}
        size='small'
        aria-label='close notification'
      >
        <CloseIcon fontSize='small' />
      </IconButton>
    </Box>
  );
};

export default Notification;
